import { db } from '@/db/client';
import { getSupabase } from '@/lib/supabase';
import type { PlayerEvent } from '@/schemas';
import type { UserId } from '@/schemas/ids';
import { encodePlayerEventToRow } from '../codec';
import { getUserSyncState, setUserLastUploadedAt } from '../state';

export interface UploadStats {
  uploaded: number;
}

export async function uploadPendingPlayerEvents(userId: UserId): Promise<UploadStats> {
  const client = getSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session?.user) {
    throw new Error('User must be authenticated to sync player events');
  }

  const { lastUploadedAt = 0 } = await getUserSyncState(userId, 'player_events');
  // Get all player events that:
  // 1. Have this userId OR don't have a userId (created before auth)
  // 2. Were created/updated after lastUploadedAt
  const allEvents: PlayerEvent[] = await db.playerEvents
    .filter((e) => e.timestamp > lastUploadedAt)
    .toArray();

  // Filter to only events owned by this user (or events without userId that we'll assign)
  const pending = allEvents.filter((e) => !e.userId || e.userId === userId);

  if (pending.length === 0) return { uploaded: 0 };

  // Ensure all events have userId set (for events created before authentication)
  const rows = pending.map((e) => ({
    ...encodePlayerEventToRow(e),
    user_id: e.userId ?? userId,
  }));
  const { error } = await client.from('player_events').upsert(rows, { onConflict: 'id' });
  if (error) throw error;

  const maxTs = pending.at(-1)?.timestamp ?? lastUploadedAt;
  await setUserLastUploadedAt(userId, 'player_events', maxTs);
  return { uploaded: pending.length };
}
